import { useLocalization } from '@lib/localization';
import { buildFolderPath, buildProjectPath, FOLDER_BASE, parseProjectLocation } from '@lib/routing/projectPath';
import { isFolder, useGetFolderByPathQuery } from '@store/api/folderEndpoints';
import { useGetProjectQuery } from '@store/api/projectEndpoints';
import { useGetTeamsQuery } from '@store/api/teamEndpoints';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { validate as uuidValidate } from 'uuid';
import NavbarBreadcrumbs from './Breadcrumbs';

export interface BreadcrumbItem {
  label: string;
  link?: string;
}

export interface DynamicBreadcrumbsProps {
  breadcrumbItems?: BreadcrumbItem[];
}

const pathKeys = [
  'Folders', 'Projects', 'Inspections', 'Checklists', 'Poi', 'Steps', 'Users', 'Server Config', 'Settings', 'Teams'
];

const DynamicBreadcrumbs: React.FC<DynamicBreadcrumbsProps> = ({ breadcrumbItems }) => {
  const location = useLocation();
  const { t } = useLocalization();
  const segments = location.pathname.split('/').filter((x) => x);
  const isTeamDetailPath = location.pathname.startsWith('/teams/');

  // A project is addressed as the leaf of its folder path, so the folder crumbs are the
  // same walk in both cases and only the project's own crumb needs its name looked up.
  const projectLocation = parseProjectLocation(location.pathname);
  const isFoldersPath = projectLocation?.base === FOLDER_BASE;

  // TeamDetail already fetches this on the same route; the subscription just reads the cache.
  const { data: teams } = useGetTeamsQuery(undefined, { skip: !isTeamDetailPath });
  // Likewise ProjectOverview: this resolves from cache rather than costing a request.
  const { data: project } = useGetProjectQuery(
    { id: projectLocation?.projectId ?? '', showError: false },
    { skip: !projectLocation?.projectId }
  );

  // Every entry point the user has — own team roots plus subtrees other teams shared with
  // them — is listed at the virtual root, so a path under one of them is reachable and
  // anything else is not. This is plain path arithmetic, not a permission decision.
  const { data: virtualRoot } = useGetFolderByPathQuery('/', { skip: !isFoldersPath });
  const reachableRoots = useMemo(
    () => virtualRoot?.children.filter(isFolder).map((child) => child.item.path) ?? [],
    [virtualRoot]
  );

  const localize = (label: string): string => {
    const matchedLocalizedLabel = pathKeys.find(pathKey => pathKey == label);

    return matchedLocalizedLabel ? t(`Common_${matchedLocalizedLabel.replace(/\s+/g, '').trim()}`) : label;
  };

  const folderTreeBreadcrumbItems = (): BreadcrumbItem[] => {
    const { folderPath, projectId } = projectLocation!;
    const folderSegments = folderPath.split('/').filter(Boolean);

    const items: BreadcrumbItem[] = [{ label: localize('Folders'), link: FOLDER_BASE }];

    folderSegments.forEach((segment, index) => {
      const walkedPath = `/${folderSegments.slice(0, index + 1).join('/')}`;
      // Inside a cross-team shared subtree the ancestors above the shared folder are
      // invisible to the user — render those crumbs without a link.
      const isReachable = reachableRoots.some(
        (rootPath) => walkedPath === rootPath || walkedPath.startsWith(`${rootPath}/`)
      );

      items.push({ label: segment, link: isReachable ? buildFolderPath(walkedPath) : undefined });
    });

    if (projectId) {
      items.push({
        label: project?.projectInfo.name ?? projectId,
        link: buildProjectPath({ folderPath, projectId }),
      });

      // Whatever follows the project — tab, inspection, poi, step — keeps the generic
      // treatment: known words are localized, ids are shown as they are.
      const tailStart = location.pathname.indexOf(projectId) + projectId.length;
      const tail = location.pathname.slice(tailStart).split('/').filter(Boolean);

      tail.forEach((segment, index) => {
        const label = uuidValidate(segment)
          ? segment
          : segment.charAt(0).toUpperCase() + segment.slice(1);

        items.push({
          label: localize(label),
          link: `${location.pathname.slice(0, tailStart)}/${tail.slice(0, index + 1).join('/')}`,
        });
      });
    }

    return items;
  };

  const genericBreadcrumbItems = (): BreadcrumbItem[] => segments.map((value, index) => {
    let label = index == 0 || !uuidValidate(value)
      ? value.split('-').map(labelPart => labelPart.charAt(0).toUpperCase() + labelPart.slice(1)).join(' ')
      : decodeURIComponent(value);

    // On /teams/:teamId show the team's name instead of its id.
    if (isTeamDetailPath && index === 1) {
      const teamName = teams?.find((team) => team._id === value)?.name;
      if (teamName) label = teamName;
    }

    return {
      label: localize(label),
      link: `/${location.pathname.split('/').slice(1, index + 2).join('/')}`,
    };
  });

  const dynamicBreadcrumbItems: BreadcrumbItem[] = [
    { label: t('Common_Home'), link: '/' },
    ...(isFoldersPath ? folderTreeBreadcrumbItems() : genericBreadcrumbItems()),
  ];

  const itemsToRender = breadcrumbItems || dynamicBreadcrumbItems;

  return <NavbarBreadcrumbs items={itemsToRender} />;
};

export default DynamicBreadcrumbs;
